import { format } from "date-fns";
import {
  Area,
  CartesianGrid,
  ComposedChart,
  Line,
  Scatter,
  XAxis,
  YAxis,
} from "recharts";

import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  ChartContainer,
  ChartLegend,
  ChartLegendContent,
  ChartTooltip,
  ChartTooltipContent,
  type ChartConfig,
} from "@/components/ui/chart";

import { MEDDREAM_LABELS } from "../labels";
import type { IMeddreamHourlyMetric } from "../schemas/meddream-hourly";

const chartConfig = {
  activeConnectionsAvg: {
    label: MEDDREAM_LABELS.avgConnections.label,
    color: "var(--chart-1)",
  },
  activeConnectionsMax: {
    label: MEDDREAM_LABELS.peakConnections.label,
    color: "var(--chart-2)",
  },
  bandTop: {
    label: MEDDREAM_LABELS.rangeMinMax.label,
    color: "var(--chart-1)",
  },
  restartMarker: {
    label: MEDDREAM_LABELS.restarts.label,
    color: "hsl(0 72% 51%)",
  },
} satisfies ChartConfig;

export interface LoadHeroChartProps {
  metrics: IMeddreamHourlyMetric[];
}

type ChartRow = {
  id: string;
  label: string;
  localLabel: string;
  activeConnectionsAvg: number;
  activeConnectionsMax: number;
  activeConnectionsMin: number;
  bandBase: number;
  bandTop: number;
  uniqueSessionsCount: number;
  restartsCount: number;
  samplesCount: number;
  restartMarker: number | null;
};

/**
 * Chart héroe: banda min–max + avg (área) + max (línea) + markers de reinicio.
 */
export function LoadHeroChart({ metrics }: LoadHeroChartProps): React.JSX.Element {
  const data: ChartRow[] = metrics.map((m) => {
    const bandBase = m.activeConnectionsMin;
    const bandTop = Math.max(0, m.activeConnectionsMax - m.activeConnectionsMin);
    return {
      id: m.id,
      label: m.id,
      localLabel: format(m.hourStart, "dd/MM HH:mm"),
      activeConnectionsAvg: m.activeConnectionsAvg,
      activeConnectionsMax: m.activeConnectionsMax,
      activeConnectionsMin: m.activeConnectionsMin,
      bandBase,
      bandTop,
      uniqueSessionsCount: m.uniqueSessionsCount,
      restartsCount: m.restartsCount,
      samplesCount: m.samplesCount,
      restartMarker: m.restartsCount > 0 ? m.activeConnectionsMax : null,
    };
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle>Carga de conexiones</CardTitle>
        <CardDescription>
          {MEDDREAM_LABELS.rangeMinMax.label}, {MEDDREAM_LABELS.avgConnections.label} y{" "}
          {MEDDREAM_LABELS.peakConnections.label} por hora (eje en hora local) · reinicios
          como marcadores
        </CardDescription>
      </CardHeader>
      <CardContent>
        {data.length === 0 ? (
          <EmptyChart />
        ) : (
          <ChartContainer config={chartConfig} className="aspect-auto h-[320px] w-full">
            <ComposedChart data={data} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
              <defs>
                <linearGradient id="avgFill" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="var(--color-activeConnectionsAvg)" stopOpacity={0.35} />
                  <stop offset="100%" stopColor="var(--color-activeConnectionsAvg)" stopOpacity={0.02} />
                </linearGradient>
              </defs>
              <CartesianGrid vertical={false} strokeDasharray="3 3" className="stroke-border/50" />
              <XAxis
                dataKey="localLabel"
                tickLine={false}
                axisLine={false}
                minTickGap={28}
                tick={{ fontSize: 11 }}
              />
              <YAxis
                tickLine={false}
                axisLine={false}
                width={36}
                tick={{ fontSize: 11 }}
                allowDecimals={false}
              />
              <ChartTooltip
                content={
                  <ChartTooltipContent
                    hideLabel={false}
                    labelFormatter={(_, payload) => {
                      const row = payload?.[0]?.payload as ChartRow | undefined;
                      if (!row) return null;
                      return (
                        <div className="space-y-0.5">
                          <div>{row.localLabel} (local)</div>
                          <div className="font-normal text-muted-foreground">
                            {row.id} UTC
                          </div>
                          <div className="font-normal text-muted-foreground">
                            {MEDDREAM_LABELS.minConnections.label}:{" "}
                            {row.activeConnectionsMin} · {MEDDREAM_LABELS.samples.label}:{" "}
                            {row.samplesCount} · {MEDDREAM_LABELS.sessionsInHour.label}:{" "}
                            {row.uniqueSessionsCount}
                          </div>
                        </div>
                      );
                    }}
                    formatter={(value, name, item) => {
                      const row = item.payload as ChartRow | undefined;
                      if (!row) return null;
                      if (
                        name === "bandTop" ||
                        name === "bandBase" ||
                        name === MEDDREAM_LABELS.rangeMinMax.label
                      ) {
                        return null;
                      }
                      if (
                        name === "restartMarker" ||
                        name === MEDDREAM_LABELS.restarts.label ||
                        name === "Reinicio"
                      ) {
                        if (!row.restartsCount) return null;
                        return (
                          <div className="flex w-full justify-between gap-4">
                            <span>{MEDDREAM_LABELS.restarts.label}</span>
                            <span className="font-mono font-medium">{row.restartsCount}</span>
                          </div>
                        );
                      }
                      return (
                        <div className="flex w-full justify-between gap-4">
                          <span>{String(name)}</span>
                          <span className="font-mono font-medium">{value}</span>
                        </div>
                      );
                    }}
                  />
                }
              />
              <ChartLegend content={<ChartLegendContent />} />
              <Area
                type="monotone"
                dataKey="bandBase"
                stackId="band"
                stroke="none"
                fill="transparent"
                isAnimationActive={false}
                legendType="none"
                tooltipType="none"
              />
              <Area
                type="monotone"
                dataKey="bandTop"
                stackId="band"
                stroke="none"
                fill="var(--color-bandTop)"
                fillOpacity={0.18}
                isAnimationActive={false}
                name={MEDDREAM_LABELS.rangeMinMax.label}
              />
              <Area
                type="monotone"
                dataKey="activeConnectionsAvg"
                stroke="var(--color-activeConnectionsAvg)"
                strokeWidth={2}
                fill="url(#avgFill)"
                name={MEDDREAM_LABELS.avgConnections.label}
                isAnimationActive={false}
              />
              <Line
                type="monotone"
                dataKey="activeConnectionsMax"
                stroke="var(--color-activeConnectionsMax)"
                strokeWidth={1.5}
                dot={false}
                name={MEDDREAM_LABELS.peakConnections.label}
                isAnimationActive={false}
              />
              <Scatter
                dataKey="restartMarker"
                fill="var(--color-restartMarker)"
                name={MEDDREAM_LABELS.restarts.label}
                isAnimationActive={false}
              />
            </ComposedChart>
          </ChartContainer>
        )}
        {data.some((d) => d.restartsCount > 0) && (
          <p className="mt-2 text-xs text-muted-foreground">
            Marcadores rojos: horas con reinicios detectados (SystemInfo).
          </p>
        )}
      </CardContent>
    </Card>
  );
}

function EmptyChart(): React.JSX.Element {
  return (
    <div className="flex h-[240px] items-center justify-center rounded-lg bg-gray-50 dark:bg-gray-900/40">
      <p className="text-sm text-muted-foreground">Sin puntos para graficar en este rango</p>
    </div>
  );
}
